import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AuthService } from '../services/auth.service';

/** Blocks a route unless someone is signed in. */
export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) {
    return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
  }
  // An account still on an administrator-set password can reach exactly one
  // screen. The API enforces this too — this only saves the user from walking
  // into a wall of refusals.
  if (auth.mustChangePassword()) {
    return router.parseUrl('/change-password');
  }
  return true;
};

/**
 * Keeps someone already signed in from landing in a different portal's area —
 * a typed URL, an old bookmark, or a stale link to `/dashboard` should not
 * hand a school admin the platform dashboard just because both sit behind the
 * same `authGuard`. Runs on every child route (not just entry), since the
 * whole app lives under one parent route and a school admin can navigate from
 * their own dashboard straight into `/dashboard` without a fresh page load.
 */
export const roleAreaGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isPathAllowedForRole(state.url, auth.role())) {
    return router.parseUrl(auth.homeRoute());
  }
  return true;
};

/**
 * A school-wide administrator holds no branch, so every branch-scoped screen
 * would refuse them until they pick one. This sends them to the branch picker
 * first, and keeps them out of it once a branch is chosen.
 */
export const branchGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // School-level screens (picking a branch, opening or editing branches,
  // managing administrators) do not depend on a branch being chosen.
  const schoolLevel = [
    '/school-admin/select-branch',
    '/school-admin/branches',
    '/school-admin/administrators',
    '/school-admin/school'
  ].some(p => state.url.startsWith(p));

  if (auth.needsBranchSelection() && !schoolLevel) {
    return router.parseUrl('/school-admin/select-branch');
  }
  if (
    !auth.needsBranchSelection() &&
    state.url.startsWith('/school-admin/select-branch') &&
    auth.role() !== 'SCHOOL_ADMIN'
  ) {
    return router.parseUrl(auth.homeRoute());
  }
  return true;
};

/** The change-password screen itself: signed in, but nowhere else to go. */
export const passwordChangeGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) {
    return router.parseUrl('/login');
  }
  return auth.mustChangePassword() ? true : router.parseUrl(auth.homeRoute());
};

/** Keeps a signed-in user away from the login page. */
export const guestGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.isAuthenticated() ? router.parseUrl(auth.homeRoute()) : true;
};
